import { resolveOrderPromotionPayment, promotionPaymentNotice } from "../supabase/edge-functions/_shared/promotionPayments";
import {
  normalizePaymentProvider,
  resolveBranchCommissionRateByChannel,
} from "../local/paymentSettings";
import {
  buildPaymentRailSnapshot,
  PAYMENT_RAILS,
} from "./paymentRails";
import {
  createInPersonQrPaymentIntent,
  normalizeKioskDigitalPaymentIntent,
} from "../supabase/kioskDigitalPaymentsService";
import {
  createMenuDigitalPaymentIntent,
  normalizeMenuDigitalPaymentIntent,
} from "../supabase/menuDigitalPaymentsService";
import {
  assertRuntimeCapability,
  RUNTIME_CAPABILITIES,
} from "../local/runtimeEnvironment";

const toSafeText = (value, maxLength = 160) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const toMoney = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const buildIntentCore = ({
  branch,
  order,
  rail,
  customerName = "",
  status = "created",
  provider = "",
  extra = {},
} = {}) => {
  const paymentRailSnapshot = buildPaymentRailSnapshot({
    source: order?.source || "",
    checkoutMethod: order?.selectedMethod || order?.requestedMethod || "",
    paymentProvider: provider || branch?.paymentProvider || "",
    splitMode: branch?.paymentSplitMode || "",
    isKioskExperience: order?.source === "kiosk",
  });
  const normalizedRail = rail || paymentRailSnapshot.rail;
  const grossAmount = toMoney(order?.totalAmount ?? order?.pricing?.finalTotal ?? 0);
  const commissionRate =
    normalizedRail === PAYMENT_RAILS.MENU_DEEPLINK
      ? resolveBranchCommissionRateByChannel(branch, "menu_qr")
      : normalizedRail === PAYMENT_RAILS.KIOSK_QR
        ? resolveBranchCommissionRateByChannel(branch, "kiosk")
        : normalizedRail === PAYMENT_RAILS.WAITER_QR
          ? resolveBranchCommissionRateByChannel(branch, "kiosk")
        : resolveBranchCommissionRateByChannel(branch, "manual");
  const currentCommissionAmount = toMoney(grossAmount * commissionRate);
  const retainedAmount = toMoney(
    extra.retainedAmount ?? extra.currentCommissionAmount ?? currentCommissionAmount
  );
  const pendingDebtAppliedAmount = toMoney(extra.pendingDebtAppliedAmount || 0);
  const merchantNetAmount = toMoney(
    extra.merchantNetAmount ?? grossAmount - retainedAmount - pendingDebtAppliedAmount
  );

  return {
    id: toSafeText(
      extra.id || `intent-${order?.paymentOperationId || order?.id || Date.now()}`,
      120
    ),
    status: toSafeText(status || extra.status || "created", 40).toLowerCase(),
    provider: normalizePaymentProvider(
      provider || extra.provider || paymentRailSnapshot.provider
    ),
    rail: normalizedRail,
    railLabel: paymentRailSnapshot.label,
    chargePresentation: paymentRailSnapshot.chargePresentation,
    commissionCollectionMode: paymentRailSnapshot.commissionCollectionMode,
    splitExecutionMode: paymentRailSnapshot.splitExecutionMode,
    interoperabilityObjective: paymentRailSnapshot.interoperabilityObjective,
    paymentSplitMode: paymentRailSnapshot.splitMode,
    externalReference: toSafeText(extra.externalReference || "", 160),
    providerOperationId: toSafeText(extra.providerOperationId || "", 160),
    customerName: toSafeText(customerName || order?.customerName || "", 80),
    grossAmount,
    retainedAmount,
    merchantNetAmount,
    currentCommissionAmount: toMoney(
      extra.currentCommissionAmount ?? currentCommissionAmount
    ),
    pendingDebtAppliedAmount,
    expiresAt: toSafeText(extra.expiresAt || "", 80),
    createdAt: toSafeText(extra.createdAt || new Date().toISOString(), 80),
    approvedAt: toSafeText(extra.approvedAt || "", 80),
    qr: extra.qr || null,
    deeplinkUrl: toSafeText(extra.deeplinkUrl || "", 1200),
    raw: extra.raw || extra,
  };
};

export const normalizePaymentIntentSnapshot = (value = {}, context = {}) => {
  const normalizedRail = buildPaymentRailSnapshot({
    source: value?.source || context?.source || "",
    checkoutMethod:
      value?.selectedMethod ||
      value?.requestedMethod ||
      context?.checkoutMethod ||
      "",
    paymentProvider:
      value?.provider || value?.paymentRailProvider || context?.paymentProvider || "",
    splitMode: value?.paymentSplitMode || context?.paymentSplitMode || "",
    isKioskExperience:
      value?.source === "kiosk" || context?.isKioskExperience === true,
  }).rail;

  if (
    normalizedRail === PAYMENT_RAILS.KIOSK_QR ||
    normalizedRail === PAYMENT_RAILS.WAITER_QR
  ) {
    const kioskIntent = normalizeKioskDigitalPaymentIntent(value);
    return buildIntentCore({
      branch: context?.branch || {},
      order: context?.order || {},
      customerName: context?.customerName || kioskIntent.raw?.customerName || "",
      rail: normalizedRail,
      provider: kioskIntent.provider,
      status: kioskIntent.status,
      extra: {
        id: kioskIntent.id,
        externalReference: kioskIntent.externalReference,
        providerOperationId: kioskIntent.providerOperationId,
        retainedAmount: kioskIntent.retainedAmount,
        merchantNetAmount: kioskIntent.merchantNetAmount,
        currentCommissionAmount: kioskIntent.currentCommissionAmount,
        pendingDebtAppliedAmount: kioskIntent.pendingDebtAppliedAmount,
        expiresAt: kioskIntent.expiresAt,
        createdAt: kioskIntent.createdAt,
        approvedAt: kioskIntent.approvedAt,
        qr: kioskIntent.qr,
        raw: kioskIntent.raw,
      },
    });
  }

  if (normalizedRail === PAYMENT_RAILS.MENU_DEEPLINK) {
    const menuIntent = normalizeMenuDigitalPaymentIntent(value);

    return buildIntentCore({
      branch: context?.branch || {},
      order: context?.order || {},
      customerName: context?.customerName || menuIntent.raw?.customerName || "",
      rail: normalizedRail,
      provider: menuIntent.provider,
      status: menuIntent.status,
      extra: {
        id: menuIntent.id,
        externalReference: menuIntent.externalReference,
        providerOperationId: menuIntent.providerOperationId,
        retainedAmount: menuIntent.retainedAmount,
        merchantNetAmount: menuIntent.merchantNetAmount,
        currentCommissionAmount: menuIntent.currentCommissionAmount,
        pendingDebtAppliedAmount: menuIntent.pendingDebtAppliedAmount,
        expiresAt: menuIntent.expiresAt,
        createdAt: menuIntent.createdAt,
        approvedAt: menuIntent.approvedAt,
        deeplinkUrl: menuIntent.deeplinkUrl,
        qr: menuIntent.qr,
        raw: menuIntent.raw,
      },
    });
  }

  return buildIntentCore({
    branch: context?.branch || {},
    order: context?.order || {},
    customerName: context?.customerName || value?.customerName || "",
    rail: normalizedRail,
    provider: value?.provider || context?.paymentProvider || "",
    status: value?.status || "created",
    extra: value,
  });
};

export const createBranchDigitalPaymentIntent = async ({
  branch,
  order,
  customerName = "",
  forceRefresh = false,
  walletProvider = "",
} = {}) => {
  assertRuntimeCapability(RUNTIME_CAPABILITIES.PAYMENT_OPERATIONS);

  const quotedOrder = resolveOrderPromotionPayment(order, "qr");
  if (quotedOrder?.totalAmount !== order?.totalAmount && quotedOrder?.pricing?.paymentPrices) {
    const format = (value) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(value);
    if (typeof window === "undefined" || !window.confirm(promotionPaymentNotice(quotedOrder.pricing, format))) {
      throw new Error("El nuevo total no fue confirmado. No se genero el cobro.");
    }
    order = quotedOrder;
  }

  const paymentRailSnapshot = buildPaymentRailSnapshot({
    source: order?.source || "",
    checkoutMethod: order?.selectedMethod || order?.requestedMethod || "",
    paymentProvider: branch?.paymentProvider || "",
    splitMode: branch?.paymentSplitMode || "",
    isKioskExperience: order?.source === "kiosk",
  });

  if (
    paymentRailSnapshot.rail === PAYMENT_RAILS.KIOSK_QR ||
    paymentRailSnapshot.rail === PAYMENT_RAILS.WAITER_QR
  ) {
    const kioskIntent = await createInPersonQrPaymentIntent({
      branch,
      order,
      customerName,
      forceRefresh,
      sourceChannel:
        paymentRailSnapshot.rail === PAYMENT_RAILS.WAITER_QR
          ? "waiter"
          : "kiosk",
    });

    return normalizePaymentIntentSnapshot(kioskIntent, {
      branch,
      order,
      customerName,
      source: order?.source || "",
      checkoutMethod: order?.selectedMethod || order?.requestedMethod || "",
      paymentProvider: branch?.paymentProvider || "",
      paymentSplitMode: branch?.paymentSplitMode || "",
      isKioskExperience: order?.source === "kiosk",
    });
  }

  if (paymentRailSnapshot.rail === PAYMENT_RAILS.MENU_DEEPLINK) {
    const menuIntent = await createMenuDigitalPaymentIntent({
      branch,
      order,
      customerName,
      forceRefresh,
      walletProvider,
      sourceChannel: order?.source || "menu_qr",
    });

    return normalizePaymentIntentSnapshot(menuIntent, {
      branch,
      order,
      customerName,
      source: order?.source || "",
      checkoutMethod: order?.selectedMethod || order?.requestedMethod || "",
      paymentProvider: branch?.paymentProvider || "",
      paymentSplitMode: branch?.paymentSplitMode || "",
      isKioskExperience: order?.source === "kiosk",
    });
  }

  return null;
};
